import { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Box, Button, Fade, Paper, TextField, Typography, Alert } from '@mui/material';
import PageTitle from '@acorn-portal/shared/src/components/text/PageTitle';
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import { UserClient } from '@acorn-portal/shared/src/services/authenticationApi';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import GetRealm from '@acorn-portal/shared/src/utils/getRealm';
import { CheckCircleOutline } from '@mui/icons-material';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface FormValues {
	email: string;
}

const validationSchema = Yup.object().shape({
	email: Yup.string().email('Invalid email format').required('Email address is required'),
});

const ResendRegistration: FC = () => {
	const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'success' | 'error'>('idle');
	const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

	useEffect(() => {
		const formSubmitted = localStorage.getItem('formSubmitted');
		if (formSubmitted === 'true') {
			setIsFormSubmitted(true);
		}
	}, []);

	const initialValues: FormValues = {
		email: '',
	};

	const onSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
		try {
			const client = new UserClient(undefined, API);
			const realm = GetRealm();
			await client.resendVerificationEmail(values.email, realm);
			localStorage.setItem('formSubmitted', 'true');
			setSubmissionStatus('success');
			setIsFormSubmitted(true);
		} catch (error) {
			console.error(error);
			setSubmissionStatus('error');
		} finally {
			setSubmitting(false);
		}
	};

	if (isFormSubmitted) {
		return (
			<Paper
				elevation={1}
				sx={{
					borderRadius: '8px',
					maxWidth: '400px',
					margin: 'auto',
					mt: 4,
				}}
			>
				<Fade in>
					<Box sx={{ p: 2 }}>
						<Box sx={{ margin: '2rem', textAlign: 'center', p: 2 }}>
							<CheckCircleOutline color='success' sx={{ fontSize: 80 }} />
							<Typography variant='h6' color='primary'>
								Your registration email has been resent successfully.
							</Typography>
						</Box>
						<Box sx={{ p: 2 }}>
							<Button
								type='submit'
								color='primary'
								variant='contained'
								fullWidth
								sx={{ px: 2 }}
								href={ROUTE_PATHS.home}
							>
								Back To Login Page
							</Button>
						</Box>
					</Box>
				</Fade>
			</Paper>
		);
	}
	return (
		<Paper
			elevation={1}
			sx={{
				borderRadius: '8px',
				maxWidth: '400px',
				margin: 'auto',
			}}
		>
			<Fade in>
				<Box sx={{ margin: '2rem', textAlign: 'center' }}>
					<PageTitle>Verify Email</PageTitle>
					<Box>
						{submissionStatus === 'error' && (
							<Alert
								severity='error'
								sx={{
									mb: 2,
									textAlign: 'left',
									svg: { width: '1.2em', height: '1.2em', margin: 'auto' },
								}}
							>
								There was an error resending the verification link. Please try again.
							</Alert>
						)}
						<Typography style={{ marginBottom: '20px', textAlign: 'left' }}>
							If you wish to resend your verification link, enter your email address below.
						</Typography>
						<Formik<FormValues> initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
							{({ isSubmitting }) => (
								<Form>
									<FormikLogErrors />
									<Field
										as={TextField}
										name='email'
										type='email'
										label='Email'
										fullWidth
										margin='normal'
										variant='outlined'
										helperText={<ErrorMessage name='email' />}
									/>
									<Button
										type='submit'
										color='primary'
										variant='contained'
										disabled={isSubmitting}
										fullWidth
										sx={{ my: 3 }}
									>
										Submit
									</Button>
								</Form>
							)}
						</Formik>
					</Box>
				</Box>
			</Fade>
		</Paper>
	);
};

export default ResendRegistration;
